import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import EventIcon from "@material-ui/icons/Event";
import DateRangeIcon from "@material-ui/icons/DateRange";
import CancelIcon from "@material-ui/icons/Cancel";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import moment from "moment";
import "moment/locale/pt-br";
import api from "../../services/api";
import toastError from "../../errors/toastError";

moment.locale("pt-br");

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 0
  },
  hero: {
    borderRadius: 24,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "radial-gradient(circle at top left, rgba(0,102,255,0.35), transparent 35%), linear-gradient(135deg, #050B18 0%, #0B1630 55%, #07111F 100%)"
        : "radial-gradient(circle at top left, rgba(0,102,255,0.16), transparent 32%), linear-gradient(135deg, #FFFFFF 0%, #F4F8FF 55%, #EAF2FF 100%)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,102,255,0.12)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 18px 50px rgba(0,0,0,0.35)"
        : "0 18px 50px rgba(15,35,80,0.08)"
  },
  heroTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  eyebrow: {
    fontSize: "0.72rem",
    fontWeight: 900,
    letterSpacing: 1.3,
    textTransform: "uppercase",
    color: "#0066FF"
  },
  heroTitle: {
    fontSize: "1.75rem",
    fontWeight: 900,
    color: theme.palette.type === "dark" ? "#fff" : "#07111F",
    marginTop: 4
  },
  heroSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.92rem",
    marginTop: 4
  },
  heroChip: {
    borderRadius: 999,
    fontWeight: 800,
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff"
  },
  kpiCard: {
    borderRadius: 20,
    padding: theme.spacing(2.2),
    minHeight: 135,
    height: "100%",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.025))"
        : "#fff",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(15,35,80,0.08)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 30px rgba(0,0,0,0.22)"
        : "0 12px 30px rgba(15,35,80,0.07)",
    position: "relative",
    overflow: "hidden"
  },
  kpiGlow: {
    position: "absolute",
    width: 90,
    height: 90,
    right: -26,
    top: -26,
    borderRadius: "50%",
    opacity: 0.14
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 1
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    boxShadow: "0 10px 22px rgba(0,102,255,0.22)"
  },
  kpiValue: {
    fontSize: "1.72rem",
    fontWeight: 900,
    marginTop: theme.spacing(1.4),
    color: theme.palette.type === "dark" ? "#fff" : "#07111F",
    position: "relative",
    zIndex: 1
  },
  kpiLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    position: "relative",
    zIndex: 1
  },
  panel: {
    borderRadius: 22,
    padding: theme.spacing(2.5),
    height: "100%",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg, rgba(255,255,255,0.055), rgba(255,255,255,0.025))"
        : "#fff",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(15,35,80,0.08)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 30px rgba(0,0,0,0.22)"
        : "0 12px 30px rgba(15,35,80,0.07)"
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  panelTitle: {
    fontWeight: 900,
    fontSize: "0.98rem",
    color: theme.palette.type === "dark" ? "#fff" : "#07111F"
  },
  rankRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.15),
    borderRadius: 15,
    marginBottom: theme.spacing(1),
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.035)"
        : "rgba(0,102,255,0.035)"
  },
  rankAvatar: {
    width: 36,
    height: 36,
    fontSize: "0.78rem",
    fontWeight: 900,
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)"
  },
  rankInfo: {
    flex: 1
  },
  rankName: {
    fontSize: "0.86rem",
    fontWeight: 800
  },
  rankCount: {
    fontWeight: 900,
    color: "#0066FF",
    minWidth: 32,
    textAlign: "right"
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    textAlign: "center",
    padding: theme.spacing(4)
  },
  insightCard: {
    borderRadius: 20,
    padding: theme.spacing(2.2),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(0,102,255,0.18), rgba(0,163,255,0.05))"
        : "linear-gradient(135deg, rgba(0,102,255,0.08), rgba(0,163,255,0.04))",
    border: "1px solid rgba(0,102,255,0.12)",
    height: "100%"
  },
  insightTitle: {
    fontWeight: 900,
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "#fff" : "#07111F",
    marginBottom: theme.spacing(1)
  },
  insightText: {
    color: theme.palette.text.secondary,
    fontSize: "0.86rem",
    lineHeight: 1.6
  }
}));

const cardConfigs = [
  { key: "appointmentsToday", label: "Agendamentos hoje", icon: EventIcon, color: "#0066FF" },
  { key: "appointmentsWeek", label: "Agenda da semana", icon: DateRangeIcon, color: "#00A3FF" },
  { key: "expectedRevenue", label: "Receita realizada", icon: AttachMoneyIcon, color: "#22C55E", prefix: "R$ " },
  { key: "cancellationRate", label: "Cancelamento / falta", icon: CancelIcon, color: "#EF4444", suffix: "%" },
  { key: "distinctContacts", label: "Clientes únicos", icon: PeopleAltIcon, color: "#F59E0B" }
];

const AgendaDashboard = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const [
        dashboardResponse,
        appointmentsResponse
      ] = await Promise.all([
        api.get("/appointments-dashboard"),
        api.get("/appointments")
      ]);

      const result =
        dashboardResponse.data || {};

      setData({
        appointmentsToday: 0,
        appointmentsWeek: 0,
        cancellationRate: 0,
        expectedRevenue: 0,
        distinctContacts: 0,
        topServices: [],
        topProfessionals: [],
        ...result
      });

      setAppointments(
        Array.isArray(
          appointmentsResponse.data
        )
          ? appointmentsResponse.data
          : []
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const formatValue = (key, value) => {
    const config = cardConfigs.find(c => c.key === key);
    let formatted = value || 0;

    if (key === "expectedRevenue") {
      formatted = Number(formatted).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    return `${config?.prefix || ""}${formatted}${config?.suffix || ""}`;
  };

  const getInitials = name =>
    String(name || "?")
      .split(" ")
      .slice(0, 2)
      .map(n => n[0])
      .join("")
      .toUpperCase();

  if (loading || !data) {
    return (
      <Paper className={classes.panel}>
        <LinearProgress />
      </Paper>
    );
  }

  const topProfessional =
    data.topProfessionals?.[0];

  const topService =
    data.topServices?.[0];

  const now = moment();

  const todayAppointments =
    appointments
      .filter(
        appointment =>
          moment(
            appointment.startTime
          ).isSame(now, "day") &&
          appointment.status !==
            "cancelado"
      )
      .sort(
        (a, b) =>
          new Date(a.startTime) -
          new Date(b.startTime)
      );

  const nextAppointments =
    todayAppointments
      .filter(
        appointment =>
          moment(
            appointment.endTime
          ).isAfter(now)
      )
      .slice(0, 8);

  const confirmedToday =
    todayAppointments.filter(
      appointment =>
        appointment.status ===
        "confirmado"
    ).length;

  const waitingToday =
    todayAppointments.filter(
      appointment =>
        appointment.status ===
        "agendado"
    ).length;

  const withoutLocationToday =
    todayAppointments.filter(
      appointment =>
        !appointment.locationId &&
        !appointment.location?.id
    ).length;

  const teamToday =
    Array.from(
      todayAppointments.reduce(
        (map, appointment) => {
          const professional =
            appointment.professional ||
            appointment.user;

          const id =
            professional?.id ||
            appointment.professionalId;

          if (!id) return map;

          const key = String(id);

          if (!map.has(key)) {
            map.set(key, {
              id,
              name:
                professional?.name ||
                `Profissional ${id}`,
              count: 0
            });
          }

          map.get(key).count += 1;

          return map;
        },
        new Map()
      ).values()
    ).sort(
      (a, b) => b.count - a.count
    );

  const unitsToday =
    Array.from(
      todayAppointments.reduce(
        (map, appointment) => {
          const id =
            appointment.location?.id ||
            appointment.locationId;

          if (!id) return map;

          const key = String(id);

          if (!map.has(key)) {
            map.set(key, {
              id,
              name:
                appointment.location?.name ||
                `Unidade ${id}`,
              count: 0
            });
          }

          map.get(key).count += 1;

          return map;
        },
        new Map()
      ).values()
    ).sort(
      (a, b) => b.count - a.count
    );

  const statusConfig = {
    agendado: {
      label: "Agendado",
      color: "#0066FF",
      bg: "#EEF5FF"
    },
    confirmado: {
      label: "Confirmado",
      color: "#059669",
      bg: "#ECFDF5"
    },
    em_atendimento: {
      label: "Em atendimento",
      color: "#D97706",
      bg: "#FFFBEB"
    },
    finalizado: {
      label: "Finalizado",
      color: "#475569",
      bg: "#F1F5F9"
    },
    nao_compareceu: {
      label: "Não compareceu",
      color: "#64748B",
      bg: "#F1F5F9"
    }
  };

  const nextAppointment =
    nextAppointments[0];

  const performanceCards =
    cardConfigs.filter(
      config =>
        config.key !==
        "appointmentsToday"
    );

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.heroTop}>
          <div>
            <Typography className={classes.eyebrow}>
              Operação de hoje
            </Typography>

            <Typography className={classes.heroTitle}>
              {moment().format(
                "dddd, DD [de] MMMM"
              )}
            </Typography>

            <Typography className={classes.heroSubtitle}>
              {nextAppointment
                ? `Próximo atendimento às ${moment(
                    nextAppointment.startTime
                  ).format("HH:mm")} • ${
                    nextAppointment.contact?.name ||
                    "Cliente"
                  } • ${
                    nextAppointment.professional?.name ||
                    nextAppointment.user?.name ||
                    "Profissional"
                  }`
                : "Nenhum próximo atendimento programado para hoje."}
            </Typography>
          </div>

          <Chip
            label="Korax IA conectada"
            className={classes.heroChip}
            size="small"
          />
        </div>
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div>
                <Typography className={classes.panelTitle}>
                  Próximos atendimentos
                </Typography>

                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  A agenda operacional do restante do dia
                </Typography>
              </div>

              <Chip
                size="small"
                label={`${nextAppointments.length} próximos`}
              />
            </div>

            {nextAppointments.length === 0 ? (
              <Typography className={classes.emptyText}>
                Não existem próximos atendimentos hoje.
              </Typography>
            ) : (
              nextAppointments.map(
                appointment => {
                  const status =
                    statusConfig[
                      appointment.status
                    ] ||
                    statusConfig.agendado;

                  return (
                    <div
                      className={classes.rankRow}
                      key={appointment.id}
                      style={{
                        background: "#F8FAFC"
                      }}
                    >
                      <div
                        style={{
                          minWidth: 58,
                          fontWeight: 900,
                          color: "#0F172A",
                          fontSize: ".86rem"
                        }}
                      >
                        {moment(
                          appointment.startTime
                        ).format("HH:mm")}
                      </div>

                      <div className={classes.rankInfo}>
                        <Typography
                          className={classes.rankName}
                        >
                          {appointment.contact?.name ||
                            "Cliente"}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            display: "block",
                            marginTop: 2
                          }}
                        >
                          {appointment.service?.name ||
                            "Serviço"}
                          {" • "}
                          {appointment.professional?.name ||
                            appointment.user?.name ||
                            "Profissional"}
                          {" • "}
                          {appointment.location?.name ||
                            "Sem unidade"}
                        </Typography>
                      </div>

                      <Chip
                        size="small"
                        label={status.label}
                        style={{
                          background: status.bg,
                          color: status.color,
                          fontWeight: 800
                        }}
                      />
                    </div>
                  );
                }
              )
            )}
          </div>
        </Grid>

        <Grid item xs={12} md={4}>
          <div
            style={{
              display: "grid",
              gap: 16
            }}
          >
            <div className={classes.panel}>
              <div className={classes.panelHeader}>
                <Typography className={classes.panelTitle}>
                  Equipe hoje
                </Typography>

                <Chip
                  size="small"
                  label={`${teamToday.length} profissionais`}
                />
              </div>

              {teamToday.length === 0 ? (
                <Typography className={classes.emptyText}>
                  Nenhum profissional com agenda hoje.
                </Typography>
              ) : (
                teamToday.map(
                  professional => (
                    <div
                      className={classes.rankRow}
                      key={professional.id}
                    >
                      <Avatar
                        className={classes.rankAvatar}
                      >
                        {getInitials(
                          professional.name
                        )}
                      </Avatar>

                      <div className={classes.rankInfo}>
                        <Typography
                          className={classes.rankName}
                        >
                          {professional.name}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {professional.count}
                          {" "}
                          atendimento
                          {professional.count !== 1
                            ? "s"
                            : ""}
                        </Typography>
                      </div>

                      <Typography
                        className={classes.rankCount}
                      >
                        {professional.count}
                      </Typography>
                    </div>
                  )
                )
              )}
            </div>

            <div className={classes.panel}>
              <div className={classes.panelHeader}>
                <Typography className={classes.panelTitle}>
                  Unidades hoje
                </Typography>

                <Chip
                  size="small"
                  label={`${unitsToday.length} unidades`}
                />
              </div>

              {unitsToday.length === 0 ? (
                <Typography className={classes.emptyText}>
                  Nenhuma unidade com atendimento hoje.
                </Typography>
              ) : (
                unitsToday.map(unit => (
                  <div
                    className={classes.rankRow}
                    key={unit.id}
                  >
                    <Avatar
                      className={classes.rankAvatar}
                    >
                      {getInitials(unit.name)}
                    </Avatar>

                    <div className={classes.rankInfo}>
                      <Typography
                        className={classes.rankName}
                      >
                        {unit.name}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="textSecondary"
                      >
                        {unit.count}
                        {" "}
                        atendimento
                        {unit.count !== 1
                          ? "s"
                          : ""}
                      </Typography>
                    </div>

                    <Typography
                      className={classes.rankCount}
                    >
                      {unit.count}
                    </Typography>
                  </div>
                ))
              )}
            </div>
          </div>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        style={{ marginTop: 8 }}
      >
        {performanceCards.map(config => {
          const Icon = config.icon;

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={config.key}
            >
              <div className={classes.kpiCard}>
                <div
                  className={classes.kpiGlow}
                  style={{
                    background:
                      config.color
                  }}
                />

                <div className={classes.cardTop}>
                  <div
                    className={classes.iconWrap}
                    style={{
                      background:
                        config.color
                    }}
                  >
                    <Icon fontSize="small" />
                  </div>
                </div>

                <Typography
                  className={classes.kpiValue}
                >
                  {formatValue(
                    config.key,
                    data[config.key]
                  )}
                </Typography>

                <Typography
                  className={classes.kpiLabel}
                >
                  {config.label}
                </Typography>
              </div>
            </Grid>
          );
        })}
      </Grid>

      <div
        className={classes.insightCard}
        style={{ marginTop: 16 }}
      >
        <Typography className={classes.insightTitle}>
          Leitura rápida da operação
        </Typography>

        <Typography className={classes.insightText}>
          Hoje existem {todayAppointments.length} atendimentos ativos,
          {" "}
          {confirmedToday} confirmados e
          {" "}
          {waitingToday} aguardando confirmação.

          {withoutLocationToday > 0
            ? ` Atenção: ${withoutLocationToday} atendimento${
                withoutLocationToday !== 1 ? "s" : ""
              } ainda ${
                withoutLocationToday !== 1 ? "estão" : "está"
              } sem unidade definida.`
            : " Todos os atendimentos de hoje possuem unidade definida."}

          {topService
            ? ` O serviço mais agendado no período é ${topService.name}.`
            : ""}

          {topProfessional
            ? ` O profissional com maior volume no período é ${topProfessional.name}.`
            : ""}
        </Typography>
      </div>
    </div>
  );

};

export default AgendaDashboard;
